import React, { useState } from 'react';
import { motion } from 'framer-motion';

const smoothScrollTo = (elementId, duration = 1000) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  const start = window.scrollY || document.documentElement.scrollTop;
  const target = element.getBoundingClientRect().top + start;
  const distance = target - start;
  let startTime = null;

  const easeInOutQuad = (t, b, c, d) => {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  const scroll = (currentTime) => {
    if (startTime === null) startTime = currentTime;
    const elapsed = currentTime - startTime;
    const run = easeInOutQuad(elapsed, start, distance, duration);

    window.scrollTo(0, run);

    if (elapsed < duration) {
      requestAnimationFrame(scroll);
    }
  };

  requestAnimationFrame(scroll);
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScrollToSection = (sectionId) => {
    smoothScrollTo(sectionId, 1200);
    setIsMobileMenuOpen(false);
  };

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      isScrolled
        ? 'bg-[#050505]/80 backdrop-blur-lg shadow-lg shadow-purple-500/20 border-b border-purple-500/20'
        : 'bg-[#050505]/50 backdrop-blur-md'
    } text-white`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-5 flex justify-between items-center">
        {/* Logo Section */}
        <div className="flex items-center gap-3 group cursor-pointer">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center font-bold text-white shadow-lg group-hover:shadow-purple-500/50 group-hover:scale-110 transition-all duration-300 transform">
            GS
          </div>
          <div className="flex flex-col">
            <h2 className="text-xl md:text-2xl font-black gradient-text group-hover:scale-105 transition-transform duration-300 origin-left">
              Golam Sarwar
            </h2>
            <span className="text-xs md:text-sm text-purple-300 font-medium">Full Stack Developer</span>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="hidden md:flex items-center gap-8">
          <motion.button
            type="button"
            onClick={() => handleScrollToSection('hero')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
            className="text-white hover:text-purple-300 transition-colors duration-300 font-semibold text-sm relative group bg-transparent border-none cursor-pointer p-0"
          >
            Home
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-400 group-hover:w-full transition-all duration-300"></span>
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('about')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
            className="text-white hover:text-purple-300 transition-colors duration-300 font-semibold text-sm relative group bg-transparent border-none cursor-pointer p-0"
          >
            About
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-400 group-hover:w-full transition-all duration-300"></span>
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('skills')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
            className="text-white hover:text-purple-300 transition-colors duration-300 font-semibold text-sm relative group bg-transparent border-none cursor-pointer p-0"
          >
            Skills
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-400 group-hover:w-full transition-all duration-300"></span>
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('projects')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
            className="text-white hover:text-purple-300 transition-colors duration-300 font-semibold text-sm relative group bg-transparent border-none cursor-pointer p-0"
          >
            Projects
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-400 group-hover:w-full transition-all duration-300"></span>
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('contact')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
            className="text-white hover:text-purple-300 transition-colors duration-300 font-semibold text-sm relative group bg-transparent border-none cursor-pointer p-0"
          >
            Contact
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-400 group-hover:w-full transition-all duration-300"></span>
          </motion.button>
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden relative group px-3 py-2 rounded-lg font-semibold transition-all duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 hover:border-purple-400/50 flex items-center gap-2"
          >
            <motion.div
              animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </motion.div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{
          opacity: isMobileMenuOpen ? 1 : 0,
          height: isMobileMenuOpen ? 'auto' : 0,
        }}
        transition={{ duration: 0.3 }}
        className="md:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-lg border-t border-purple-500/20"
      >
        <div className="flex flex-col gap-2 p-4 space-y-2">
          <motion.button
            type="button"
            onClick={() => handleScrollToSection('hero')}
            whileTap={{ scale: 0.95 }}
            className="w-full text-left px-4 py-3 text-white hover:bg-purple-500/20 transition-all duration-300 font-semibold rounded-lg"
          >
            🏠 Home
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('about')}
            whileTap={{ scale: 0.95 }}
            className="w-full text-left px-4 py-3 text-white hover:bg-purple-500/20 transition-all duration-300 font-semibold rounded-lg"
          >
            👤 About
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('skills')}
            whileTap={{ scale: 0.95 }}
            className="w-full text-left px-4 py-3 text-white hover:bg-purple-500/20 transition-all duration-300 font-semibold rounded-lg"
          >
            💪 Skills
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('projects')}
            whileTap={{ scale: 0.95 }}
            className="w-full text-left px-4 py-3 text-white hover:bg-purple-500/20 transition-all duration-300 font-semibold rounded-lg"
          >
            🎯 Projects
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleScrollToSection('contact')}
            whileTap={{ scale: 0.95 }}
            className="w-full text-left px-4 py-3 text-white hover:bg-purple-500/20 transition-all duration-300 font-semibold rounded-lg"
          >
            💬 Contact
          </motion.button>
        </div>
      </motion.div>
    </nav>
  );
};

export default Navbar;